import {inject, Injectable} from "@angular/core"
import {IsUnique} from "@cm/utils/filter"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {BatchApiCallById} from "@common/helpers/batch-api-call/batch-api-call-by-id"
import {ApiRequest} from "@common/models/api-request/api-request"
import {DataObjectThumbnailFragment, QueryDataObjectThumbnailsGQL} from "@common/services/data-object-thumbnail/data-object-thumbnail.service.generated"
import {DownloadResolution} from "@generated"

export type RequestPayload = {
    id: string
    resolution: DownloadResolution
}

export type ResponsePayload = DataObjectThumbnailFragment

export type BatchedRequestPayload = {
    requests: ApiRequest<RequestPayload, ResponsePayload>[]
    resolution: DownloadResolution
}

@Injectable({
    providedIn: "root",
})
export class DataObjectThumbnailService extends BatchApiCallById<RequestPayload, ResponsePayload, BatchedRequestPayload> {
    readonly dataObjectThumbnailsGql = inject(QueryDataObjectThumbnailsGQL)

    constructor() {
        super()
    }

    protected batchRequests(requests: ApiRequest<RequestPayload, ResponsePayload>[]): BatchedRequestPayload[] {
        // separate the requests by resolution and data object id
        const requestsByResolution = new Map<DownloadResolution, BatchedRequestPayload>()
        const results: BatchedRequestPayload[] = []
        const maxBatchSize = 10
        requests.forEach((request) => {
            const resolution = request.payload.resolution
            if (!requestsByResolution.has(resolution)) {
                requestsByResolution.set(resolution, {requests: [], resolution})
            }
            const mapEntry = requestsByResolution.get(request.payload.resolution)!
            mapEntry.requests.push(request)
            // don't exceed the maximum batch size to avoid hitting the GQL limit
            if (mapEntry.requests.length >= maxBatchSize) {
                results.push({requests: mapEntry.requests, resolution: mapEntry.resolution})
                requestsByResolution.delete(request.payload.resolution)
            }
        })
        return [...results, ...Array.from(requestsByResolution.values())]
    }

    protected async callApi(payload: BatchedRequestPayload): Promise<(ResponsePayload | undefined | null)[]> {
        const ids = payload.requests.map((request) => request.payload.id).filter(IsUnique)
        return fetchThrowingErrors(this.dataObjectThumbnailsGql)({
            ids,
            resolution: payload.resolution,
        }).then((response) => response.dataObjectsById)
    }
}
